// Plany wędzeń (`/api/plany`, PLAN-ZROWNANIA etap 4): stan poza Reactem jak wnioski (api/przypisanie.ts) - wczytywany
// przy wejściu na ekran i po zdarzeniu SSE `konto` (`plany.zmiana` z innego telefonu albo od innej osoby z dostępem do
// wędzarni). Zmiana z wersją: konflikt (409) oddaje aktualny plan - telefon go pokazuje. Listy i dni liczy worker.
import { useSyncExternalStore } from "react";
import type {
  AccountEvent, AkcjaEtapu, OKReply, PlanChanged, PlanCreateIn, PlanDeviceOut, PlanOut, PlanReply, PlanSaveIn, PlansReply,
} from "../gen/kontrakt";
import { zapytanie } from "./zaplecze";

interface Stan {
  wczytane: boolean;
  plany: readonly PlanOut[];
  wedzarnie: readonly PlanDeviceOut[];
}

let stan: Stan = { wczytane: false, plany: [], wedzarnie: [] };
const sluchacze = new Set<() => void>();
let wczytywanie: Promise<void> | null = null;

function ustaw(s: Stan): void {
  stan = s;
  for (const f of sluchacze) f();
}

/** Wczytuje plany konta (jedno zapytanie naraz); błąd - zostaje poprzedni stan. */
export function wczytajPlany(): Promise<void> {
  wczytywanie ??= zapytanie<PlansReply>("GET", "/api/plany")
    .then((o) => ustaw({ wczytane: true, plany: o.plany, wedzarnie: o.wedzarnie }), () => undefined)
    .finally(() => { wczytywanie = null; });
  return wczytywanie;
}

/** Plan po zapisie albo konflikcie - podmiana na liście (najbliższy na górze zostaje z serwera przy wczytaniu). */
function podmien(p: PlanOut): void {
  const jest = stan.plany.some((x) => x.id === p.id);
  ustaw({ ...stan, plany: jest ? stan.plany.map((x) => (x.id === p.id ? p : x)) : [...stan.plany, p] });
}

/** Zdarzenie SSE `konto`: plan zmieniony gdzie indziej - pobranie, gdy lokalnie jest inna wersja. */
export function przyjmijZdarzeniePlanow(e: AccountEvent): void {
  if (e.rodzaj !== "plany.zmiana" || !stan.wczytane) return;
  const z = e.dane as PlanChanged;
  if (z.wersja !== 0 && stan.plany.find((x) => x.id === z.id)?.wersja === z.wersja) return;
  void wczytajPlany();
}

/** Wylogowanie: bez planów poprzedniego konta. */
export function wyczyscPlany(): void {
  ustaw({ wczytane: false, plany: [], wedzarnie: [] });
}

export async function nowyPlan(p: PlanCreateIn): Promise<PlanOut> {
  const o = await zapytanie<PlanReply>("POST", "/api/plany", p);
  podmien(o.plan);
  return o.plan;
}

/** Podgląd harmonogramu w formularzu „Zaplanuj” (#29) - plan jak po zapisie, bez zapisu. */
export async function podgladPlanu(p: PlanCreateIn): Promise<PlanOut> {
  return (await zapytanie<PlanReply>("POST", "/api/plany/podglad", p)).plan;
}

/** Zapis planu; konflikt wersji - błąd zapytania, lista od nowa z serwera. */
export async function zapiszPlan(id: number, p: PlanSaveIn): Promise<void> {
  try {
    podmien((await zapytanie<PlanReply>("PUT", `/api/plany/${id}`, p)).plan);
  } catch (e) {
    void wczytajPlany();
    throw e;
  }
}

/** „To wędzenie z tego planu” - przebieg z kandydatów planu. */
export async function podepnijPrzebieg(id: number, wersja: number, przebieg: number): Promise<void> {
  try {
    podmien((await zapytanie<PlanReply>("POST", `/api/plany/${id}/przebieg`, { wersja, przebieg })).plan);
  } catch (e) {
    void wczytajPlany();
    throw e;
  }
}

/** Etap harmonogramu: „Zrobione” (odhacza jego pozycje), „cofnij” albo „Odłóż 1 h” przypomnienia - bez wersji
 *  (ta sama trasa co przycisk w powiadomieniu, sw.js). */
export async function akcjaEtapu(id: number, etap: string, akcja: AkcjaEtapu): Promise<void> {
  try {
    podmien((await zapytanie<PlanReply>("POST", `/api/plany/${id}/etapy/${encodeURIComponent(etap)}`, { akcja })).plan);
  } catch (e) {
    void wczytajPlany();
    throw e;
  }
}

export async function usunPlan(id: number): Promise<void> {
  await zapytanie<OKReply>("DELETE", `/api/plany/${id}`);
  ustaw({ ...stan, plany: stan.plany.filter((x) => x.id !== id) });
}

function subskrybuj(f: () => void): () => void {
  sluchacze.add(f);
  return () => sluchacze.delete(f);
}

export function usePlany(): Stan {
  return useSyncExternalStore(subskrybuj, () => stan);
}
